// Utility functions for tracking if user's mouse follows the stroke path

/**
 * Calculate distance between two points
 */
export function distance(p1, p2) {
  const dx = p2.x - p1.x
  const dy = p2.y - p1.y
  return Math.sqrt(dx * dx + dy * dy)
}

/**
 * Find the closest point on a line segment to a given point
 */
export function closestPointOnSegment(point, segmentStart, segmentEnd) {
  const dx = segmentEnd.x - segmentStart.x
  const dy = segmentEnd.y - segmentStart.y
  const lengthSquared = dx * dx + dy * dy

  if (lengthSquared === 0) return segmentStart

  const t = Math.max(0, Math.min(1,
    ((point.x - segmentStart.x) * dx + (point.y - segmentStart.y) * dy) / lengthSquared
  ))

  return {
    x: segmentStart.x + t * dx,
    y: segmentStart.y + t * dy
  }
}

/**
 * Check if a point is near any segment of the path
 * @param {Object} point - {x, y} coordinates
 * @param {Array} pathPoints - Array of {x, y} points forming the path
 * @param {number} threshold - Maximum distance to be considered "on path"
 * @returns {boolean}
 */
export function isPointNearPath(point, pathPoints, threshold = 20) {
  for (let i = 0; i < pathPoints.length - 1; i++) {
    const closest = closestPointOnSegment(point, pathPoints[i], pathPoints[i + 1])
    const dist = distance(point, closest)
    if (dist <= threshold) {
      return true
    }
  }
  return false
}

/**
 * Check if user has traced the path in the correct order
 * @param {Array} userPath - Array of {x, y} points drawn by user
 * @param {Array} correctPath - Array of {x, y} points of correct stroke (checkpoints)
 * @param {number} threshold - Distance threshold (deprecated, use getThresholdForCheckpoint instead)
 * @returns {Object} - {isCorrect: boolean, coverage: number (0-1), checkpointsCleared: Array, clearedCheckpoints: Set}
 */
export function evaluateTracePath(userPath, correctPath, threshold = 35) {
  if (userPath.length === 0) {
    return {
      isCorrect: false,
      coverage: 0,
      checkpointsCleared: [],
      clearedCheckpoints: new Set()
    }
  }

  // Get threshold for each checkpoint (2x the dot radius)
  const getThresholdForCheckpoint = (idx) => {
    // Starting dot radius = 10.5, so threshold = 21
    // Regular dot radius = 7.5, so threshold = 15
    return idx === 0 ? 21 : 15
  }

  // Track which checkpoints (actual path points) have been cleared
  const checkpointsCleared = new Array(correctPath.length).fill(false)

  // Track the highest checkpoint cleared so far (to enforce sequential order)
  let highestCheckpointCleared = -1

  // Check each user point to see if it clears the NEXT checkpoint only
  for (const userPoint of userPath) {
    // Only check the very next checkpoint in sequence
    const nextCheckpoint = highestCheckpointCleared + 1
    if (nextCheckpoint < correctPath.length) {
      const dist = distance(userPoint, correctPath[nextCheckpoint])
      const checkpointThreshold = getThresholdForCheckpoint(nextCheckpoint)
      if (dist <= checkpointThreshold) {
        checkpointsCleared[nextCheckpoint] = true
        highestCheckpointCleared = nextCheckpoint
      }
    }
  }

  // Calculate how many checkpoints were cleared
  const clearedCount = checkpointsCleared.filter(Boolean).length
  const checkpointCoverage = clearedCount / correctPath.length

  // User must have cleared ALL checkpoints in sequential order
  const allCheckpointsCleared = clearedCount === correctPath.length
  const isCorrect = allCheckpointsCleared

  return {
    isCorrect,
    checkpointsCleared,
    clearedCheckpoints: new Set(checkpointsCleared.map((cleared, idx) => cleared ? idx : -1).filter(idx => idx >= 0)),
    totalCheckpoints: correctPath.length,
    clearedCount,
    checkpointCoverage
  }
}

/**
 * Calculate total path length
 */
export function calculatePathLength(pathPoints) {
  let totalLength = 0
  for (let i = 0; i < pathPoints.length - 1; i++) {
    totalLength += distance(pathPoints[i], pathPoints[i + 1])
  }
  return totalLength
}

/**
 * Calculate the minimum distance (deviation) from a point to the path
 * @param {Object} userPoint - {x, y} coordinates of the user's current point
 * @param {Array} pathPoints - Array of {x, y} points forming the ideal path
 * @returns {Object} - {distance: number, segmentIndex: number}
 */
export function calculateDeviationFromPath(userPoint, pathPoints) {
  if (!pathPoints || pathPoints.length === 0) {
    return { distance: Infinity, segmentIndex: -1 }
  }

  // For single-point paths (dots), calculate distance to that point
  if (pathPoints.length === 1) {
    return {
      distance: distance(userPoint, pathPoints[0]),
      segmentIndex: 0
    }
  }

  let minDistance = Infinity
  let closestSegmentIndex = 0

  for (let i = 0; i < pathPoints.length - 1; i++) {
    const closest = closestPointOnSegment(userPoint, pathPoints[i], pathPoints[i + 1])
    const dist = distance(userPoint, closest)
    if (dist < minDistance) {
      minDistance = dist
      closestSegmentIndex = i
    }
  }

  return {
    distance: minDistance,
    segmentIndex: closestSegmentIndex
  }
}
